/**
 * Go Error Handler
 *
 * Wraps ErrorHandlingService for game-specific error classification.
 * Follows ReversiErrorHandler pattern.
 */

import {
  ErrorHandlingService,
  ErrorType,
  ErrorSeverity,
} from '../../services/ErrorHandlingService';
import {
  NotificationService,
  NotificationType,
} from '../../services/NotificationService';

export enum GoErrorType {
  INVALID_MOVE = 'INVALID_MOVE',
  NOT_YOUR_TURN = 'NOT_YOUR_TURN',
  GAME_ENDED = 'GAME_ENDED',
  GAME_LOGIC = 'GAME_LOGIC',
  KO_VIOLATION = 'KO_VIOLATION',
  SUICIDE_MOVE = 'SUICIDE_MOVE',
  CONNECTION_ERROR = 'CONNECTION_ERROR',
  CORRUPTED_DATA = 'CORRUPTED_DATA',
  AI_TIMEOUT = 'AI_TIMEOUT',
  MULTIPLAYER_VALIDATION = 'MULTIPLAYER_VALIDATION',
  SAVE_ERROR = 'SAVE_ERROR',
  LOAD_ERROR = 'LOAD_ERROR',
  UNKNOWN = 'UNKNOWN',
}

export interface GoError {
  type: GoErrorType;
  message: string;
  details?: any;
  timestamp: number;
  recoverable: boolean;
}

export class GoErrorHandler {
  private errorService: ErrorHandlingService;
  private notificationService: NotificationService;
  private errorLog: GoError[] = [];
  private readonly MAX_ERROR_LOG = 50;

  constructor(
    errorService: ErrorHandlingService,
    notificationService: NotificationService
  ) {
    this.errorService = errorService;
    this.notificationService = notificationService;
  }

  handleError(
    type: GoErrorType,
    message: string,
    details?: any,
    showNotification: boolean = true
  ): GoError {
    const error: GoError = {
      type,
      message,
      details,
      timestamp: Date.now(),
      recoverable: this.isRecoverable(type),
    };

    this.logError(error);

    const { errorType, severity } = this.classifyError(type);
    this.errorService.handleError(
      new Error(message),
      errorType,
      severity,
      { component: 'Go', additionalData: details },
      { showNotification: false }
    );

    if (showNotification) {
      this.showErrorNotification(error);
    }

    return error;
  }

  private classifyError(
    type: GoErrorType
  ): { errorType: ErrorType; severity: ErrorSeverity } {
    switch (type) {
      case GoErrorType.INVALID_MOVE:
      case GoErrorType.NOT_YOUR_TURN:
      case GoErrorType.GAME_ENDED:
      case GoErrorType.GAME_LOGIC:
      case GoErrorType.KO_VIOLATION:
      case GoErrorType.SUICIDE_MOVE:
      case GoErrorType.AI_TIMEOUT:
        return { errorType: ErrorType.GAME_LOGIC, severity: ErrorSeverity.LOW };

      case GoErrorType.CONNECTION_ERROR:
      case GoErrorType.MULTIPLAYER_VALIDATION:
        return { errorType: ErrorType.MULTIPLAYER, severity: ErrorSeverity.MEDIUM };

      case GoErrorType.CORRUPTED_DATA:
      case GoErrorType.SAVE_ERROR:
      case GoErrorType.LOAD_ERROR:
        return { errorType: ErrorType.STORAGE, severity: ErrorSeverity.MEDIUM };

      default:
        return { errorType: ErrorType.SYSTEM, severity: ErrorSeverity.MEDIUM };
    }
  }

  // Convenience methods
  handleGameLogicError(reason: string): GoError {
    return this.handleError(
      GoErrorType.GAME_LOGIC,
      `Game error: ${reason}`,
      { reason }
    );
  }

  handleInvalidMove(position: string): GoError {
    return this.handleError(
      GoErrorType.INVALID_MOVE,
      `Invalid move at ${position}.`,
      { position }
    );
  }

  handleKoViolation(): GoError {
    return this.handleError(
      GoErrorType.KO_VIOLATION,
      'Ko rule: cannot recapture immediately.',
      undefined
    );
  }

  handleSuicideMove(): GoError {
    return this.handleError(
      GoErrorType.SUICIDE_MOVE,
      'Suicide move: placing here would leave your group with no liberties.',
      undefined
    );
  }

  handleNotYourTurn(): GoError {
    return this.handleError(
      GoErrorType.NOT_YOUR_TURN,
      "It's not your turn. Please wait.",
      undefined
    );
  }

  handleConnectionError(error: any): GoError {
    return this.handleError(
      GoErrorType.CONNECTION_ERROR,
      'Connection lost. Attempting to reconnect...',
      error,
      true
    );
  }

  handleCorruptedData(dataType: string, error: any): GoError {
    return this.handleError(
      GoErrorType.CORRUPTED_DATA,
      'Unable to load saved game. Starting new game.',
      { dataType, error },
      true
    );
  }

  handleSaveError(gameId: string, error: any): GoError {
    return this.handleError(
      GoErrorType.SAVE_ERROR,
      'Failed to save game. Your progress may be lost.',
      { gameId, error },
      true
    );
  }

  handleLoadError(gameId: string, error: any): GoError {
    return this.handleError(
      GoErrorType.LOAD_ERROR,
      'Failed to load game. Starting new game.',
      { gameId, error },
      true
    );
  }

  // Internal helpers
  private isRecoverable(type: GoErrorType): boolean {
    switch (type) {
      case GoErrorType.INVALID_MOVE:
      case GoErrorType.NOT_YOUR_TURN:
      case GoErrorType.GAME_ENDED:
      case GoErrorType.GAME_LOGIC:
      case GoErrorType.KO_VIOLATION:
      case GoErrorType.SUICIDE_MOVE:
      case GoErrorType.CONNECTION_ERROR:
      case GoErrorType.CORRUPTED_DATA:
      case GoErrorType.AI_TIMEOUT:
      case GoErrorType.SAVE_ERROR:
      case GoErrorType.LOAD_ERROR:
        return true;
      default:
        return false;
    }
  }

  private logError(error: GoError): void {
    this.errorLog.push(error);
    if (this.errorLog.length > this.MAX_ERROR_LOG) {
      this.errorLog.shift();
    }
  }

  private showErrorNotification(error: GoError): void {
    const notificationType = error.recoverable
      ? NotificationType.WARNING
      : NotificationType.ERROR;
    this.notificationService.addNotification({
      type: notificationType,
      title: 'Go Error',
      message: error.message,
      duration: 5000,
    });
  }

  getErrorLog(): GoError[] {
    return [...this.errorLog];
  }

  clearErrorLog(): void {
    this.errorLog = [];
  }
}
